import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { postprocessAssistantText } from '../lib/linkLabels';

interface CreatorAssistantProps {
  onBack: () => void;
  userId: string;
  onOpenCredentials: () => void;
}

const CreatorAssistant: React.FC<CreatorAssistantProps> = ({ onBack, userId, onOpenCredentials }) => {
  const [currentPrompt, setCurrentPrompt] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<{role: 'user' | 'assistant', content: string, usedFunctions?: string[]}[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const prompts = [
    "Which of my videos drove the most bookings in the last 30 days?",
    "How many bookings did I get last week vs the week before?",
    "What was my closed revenue this month?",
    "Show me my top 5 performing trackable links by close rate.",
    "Compare bookings by month for the last 6 months.",
    "Pull the transcript for my latest video and tell me what offer it pitches.",
    "Which trackable link description ('coaching', 'consultation', etc.) converts best?",
    "What days of the week generate the most bookings?",
    "Find videos in my library where I talk about pricing.",
    "Summarize the content patterns in my top 3 videos.",
    "What's my close rate this quarter vs last?",
    "Which videos have transcripts and which don't?",
    "Show booking trend by week for the last 12 weeks.",
    "Which video has the highest revenue per booking?",
    "What's the average time between a booking and a close?"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPrompt(prev => (prev + 1) % prompts.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [prompts.length]);

  // Create chat session on mount
  useEffect(() => {
    const initializeSession = async () => {
      try {
        const response = await api.chat.createSession();
        if (response.success && response.sessionId) {
          setSessionId(response.sessionId);
          console.log('Chat session created:', response.sessionId);
        }
      } catch (error) {
        console.error('Error creating chat session:', error);
      }
    };

    initializeSession();
  }, []);

  const formatIdentity = (profile: any): string => {
    if (!profile) return "No identity set yet. Type `/identity <a paragraph about your niche, audience, offer, voice>` to set one up.";
    const lines = ['**Current identity:**', '', `> ${profile.rawText}`, ''];
    const fields: [string, string | null][] = [
      ['Niche', profile.niche],
      ['Audience', profile.audience],
      ['Offer', profile.offer],
      ['Voice', profile.voice],
      ['Avoid', profile.doNots],
      ['Goal metric', profile.goalMetric],
    ];
    const populated = fields.filter(([, v]) => v);
    if (populated.length > 0) {
      lines.push('**Parsed:**');
      for (const [k, v] of populated) lines.push(`- ${k}: ${v}`);
    }
    lines.push('', 'To replace it: `/identity <new paragraph>`');
    return lines.join('\n');
  };

  const handleIdentityCommand = async (input: string) => {
    const stripped = input.replace(/^\/identity\s*/i, '').trim();
    setMessages(prev => [...prev, { role: 'user', content: input }]);
    setIsLoading(true);
    try {
      if (!stripped) {
        const data = await api.identity.get();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: formatIdentity(data?.profile)
        }]);
      } else {
        const data = await api.identity.set(stripped);
        const intro = data?.profile?.niche ? '✓ Saved your identity.' : '✓ Saved (extraction in progress — fields will populate on next chat turn).';
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `${intro}\n\n${formatIdentity(data?.profile)}`
        }]);
      }
    } catch (error: any) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Couldn't update identity: ${error?.message || 'unknown error'}`
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || !sessionId) return;

    const userMessage = inputValue.trim();
    setInputValue('');

    if (/^\/identity(\s|$)/i.test(userMessage)) {
      await handleIdentityCommand(userMessage);
      return;
    }

    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const data = await api.chat.sendMessage(sessionId, userMessage);

      if (data.success && data.response) {
        const labeled = await postprocessAssistantText(data.response);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: labeled,
          usedFunctions: data.usedFunctions
        }]);
      } else {
        throw new Error(data.error || 'Failed to get AI response');
      }
    } catch (error: any) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again later.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePromptClick = (prompt: string) => {
    setInputValue(prompt);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#ffffff',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ← Back to Dashboard
        </button>
        <h1 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#1f2937',
          margin: 0
        }}>
          Creator Assistant
        </h1>
        <div style={{ width: '40px' }}></div>
      </div>

      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '768px',
        margin: '0 auto',
        width: '100%',
        padding: '0 24px'
      }}>
        
        {/* Messages or Welcome */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          paddingTop: messages.length === 0 ? '120px' : '24px'
        }}>
          
          {messages.length === 0 ? (
            /* Welcome Screen */
            <div style={{
              textAlign: 'center',
              marginBottom: '48px'
            }}>
              <h2 style={{
                fontSize: '32px',
                fontWeight: '300',
                color: '#1f2937',
                marginBottom: '16px',
                lineHeight: '1.2'
              }}>
                How can I help you create better content?
              </h2>
              
              <div style={{
                height: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '32px'
              }}>
                <p style={{
                  fontSize: '16px',
                  color: '#6b7280',
                  margin: 0,
                  opacity: 0.8,
                  transition: 'opacity 0.5s ease-in-out'
                }}>
                  {prompts[currentPrompt]}
                </p>
              </div>

              {/* Suggested Prompts */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '12px',
                marginTop: '32px'
              }}>
                {prompts.slice(0, 6).map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handlePromptClick(prompt)}
                    style={{
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#374151',
                      transition: 'all 0.2s ease',
                      lineHeight: '1.4'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Chat Messages */
            <div style={{
              flex: 1,
              paddingBottom: '24px'
            }}>
              {messages.map((message, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: message.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <div style={{
                    maxWidth: '80%',
                    padding: '16px 20px',
                    borderRadius: message.role === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                    backgroundColor: message.role === 'user' ? '#2563eb' : '#f1f5f9',
                    color: message.role === 'user' ? 'white' : '#334155',
                    fontSize: '15px',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-line'
                  }}>
                    {message.content}
                  </div>
                  {message.role === 'assistant' && message.usedFunctions && message.usedFunctions.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px',
                      marginTop: '8px',
                      maxWidth: '80%'
                    }}>
                      {Array.from(new Set(message.usedFunctions)).map(fn => (
                        <span
                          key={fn}
                          title={`Tool used: ${fn}`}
                          style={{
                            fontSize: '11px',
                            color: '#475569',
                            backgroundColor: '#e2e8f0',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
                          }}
                        >
                          {fn}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {isLoading && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    padding: '16px 20px',
                    borderRadius: '20px 20px 20px 4px',
                    backgroundColor: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#6b7280',
                      borderRadius: '50%',
                      animation: 'pulse 1.5s ease-in-out infinite'
                    }}></div>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#6b7280',
                      borderRadius: '50%',
                      animation: 'pulse 1.5s ease-in-out infinite 0.2s'
                    }}></div>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#6b7280',
                      borderRadius: '50%',
                      animation: 'pulse 1.5s ease-in-out infinite 0.4s'
                    }}></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input Area */}
        <div style={{
          padding: '24px 0',
          borderTop: messages.length > 0 ? '1px solid #e5e7eb' : 'none'
        }}>
          <form onSubmit={handleSubmit}>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask me anything about content creation..."
                disabled={isLoading}
                style={{
                  width: '100%',
                  minHeight: '24px',
                  maxHeight: '120px',
                  padding: '16px 60px 16px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '24px',
                  fontSize: '15px',
                  lineHeight: '1.5',
                  resize: 'none',
                  outline: 'none',
                  backgroundColor: isLoading ? '#f9fafb' : 'white',
                  color: isLoading ? '#9ca3af' : '#1f2937'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                style={{
                  position: 'absolute',
                  right: '8px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: inputValue.trim() && !isLoading ? '#2563eb' : '#e5e7eb',
                  color: 'white',
                  cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s ease'
                }}
              >
                ↑
              </button>
            </div>
          </form>
          
          <p style={{
            fontSize: '12px',
            color: '#9ca3af',
            textAlign: 'center',
            marginTop: '12px',
            margin: '12px 0 0 0'
          }}>
            AI can make mistakes. Consider checking important information.
          </p>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes pulse {
          0%, 80%, 100% {
            opacity: 0.4;
          }
          40% {
            opacity: 1;
          }
        }
        
        @keyframes glow {
          0% {
            box-shadow: 0 0 5px rgba(245, 158, 11, 0.5);
          }
          100% {
            box-shadow: 0 0 20px rgba(245, 158, 11, 0.8), 0 0 30px rgba(245, 158, 11, 0.4);
          }
        }
      `}</style>
    </div>
  );
};

export default CreatorAssistant;